/**
 * Daily reference intakes for the micronutrient report (US/Canada DRIs:
 * RDA where one exists, AI otherwise; "limit" nutrients use the CDRR or the
 * Dietary Guidelines' 10%-of-calories ceilings). Adults only — Macro is not
 * built for children, and the tables stop at 19.
 */

import type { NutrientKey } from './nutrients.ts'
import type { Sex } from './targets.ts'

export type Dri = { key: NutrientKey; amount: number; kind: 'target' | 'limit' }

type Band = '19-30' | '31-50' | '51-70' | '71+'

function band(age: number): Band {
  return age <= 30 ? '19-30' : age <= 50 ? '31-50' : age <= 70 ? '51-70' : '71+'
}

// [male, female] per band.
const T: Partial<Record<NutrientKey, Record<Band, [number, number]>>> = {
  fibre_g: { '19-30': [38, 25], '31-50': [38, 25], '51-70': [30, 21], '71+': [30, 21] },
  potassium_mg: { '19-30': [3400, 2600], '31-50': [3400, 2600], '51-70': [3400, 2600], '71+': [3400, 2600] },
  calcium_mg: { '19-30': [1000, 1000], '31-50': [1000, 1000], '51-70': [1000, 1200], '71+': [1200, 1200] },
  iron_mg: { '19-30': [8, 18], '31-50': [8, 18], '51-70': [8, 8], '71+': [8, 8] },
  magnesium_mg: { '19-30': [400, 310], '31-50': [420, 320], '51-70': [420, 320], '71+': [420, 320] },
  zinc_mg: { '19-30': [11, 8], '31-50': [11, 8], '51-70': [11, 8], '71+': [11, 8] },
  vit_a_ug: { '19-30': [900, 700], '31-50': [900, 700], '51-70': [900, 700], '71+': [900, 700] },
  vit_c_mg: { '19-30': [90, 75], '31-50': [90, 75], '51-70': [90, 75], '71+': [90, 75] },
  vit_d_ug: { '19-30': [15, 15], '31-50': [15, 15], '51-70': [15, 15], '71+': [20, 20] },
  vit_e_mg: { '19-30': [15, 15], '31-50': [15, 15], '51-70': [15, 15], '71+': [15, 15] },
  vit_k_ug: { '19-30': [120, 90], '31-50': [120, 90], '51-70': [120, 90], '71+': [120, 90] },
  b12_ug: { '19-30': [2.4, 2.4], '31-50': [2.4, 2.4], '51-70': [2.4, 2.4], '71+': [2.4, 2.4] },
  folate_ug: { '19-30': [400, 400], '31-50': [400, 400], '51-70': [400, 400], '71+': [400, 400] },
}

export function driFor(sex: Sex | null | undefined, age: number, kcal: number): Dri[] {
  const b = band(Math.max(19, age))
  const i = sex === 'male' ? 0 : 1
  const out: Dri[] = []
  for (const [key, bands] of Object.entries(T) as [NutrientKey, Record<Band, [number, number]>][]) {
    out.push({ key, amount: bands[b][i], kind: 'target' })
  }
  out.push({ key: 'sodium_mg', amount: 2300, kind: 'limit' })
  out.push({ key: 'sat_fat_g', amount: Math.round((0.1 * kcal) / 9), kind: 'limit' })
  out.push({ key: 'added_sugars_g', amount: Math.round((0.1 * kcal) / 4), kind: 'limit' })
  return out
}
